import React from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useAccount } from '../../context/AccountContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface AccountSwitcherProps {
  /** Visual density for different chrome locations. */
  variant?: 'sidebar' | 'sidebar-compact' | 'header' | 'settings' | 'inline';
  /** When false, render nothing even if the user has multiple accounts. */
  showWhenSingle?: boolean;
  className?: string;
}

/**
 * Account context switcher. Visible when the signed-in user belongs to more
 * than one account (unless showWhenSingle). Selection is owned by AccountContext
 * and persisted to localStorage.
 *
 * A menu rather than a native <select>: the collapsed rail used to paint the
 * select with transparent text and no caret, so the control looked like an
 * empty box and the account names were easy to lose.
 */
export function AccountSwitcher({
  variant = 'header',
  showWhenSingle = false,
  className = '',
}: AccountSwitcherProps) {
  const { accounts, selectedAccount, selectAccountById, isLoading } = useAccount();

  if (isLoading || accounts.length === 0) return null;
  if (!showWhenSingle && accounts.length < 2) return null;

  const compact = variant === 'sidebar-compact';
  const inline = variant === 'inline';
  const currentName = selectedAccount?.name || 'Switch account';

  return (
    <div
      className={
        compact || inline
          ? className
          : variant === 'settings'
            ? `mb-6 w-full min-w-0 ${className}`
            : variant === 'sidebar'
              ? `w-full min-w-0 ${className}`
              : `min-w-[200px] ${className}`
      }
    >
      {!compact && !inline && variant !== 'sidebar' && (
        <p className="mb-1 block text-sm font-medium text-cmp-ink">
          {variant === 'settings' ? 'Account' : 'Select Account'}
        </p>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger
          type="button"
          className={
            inline
              ? 'inline-flex items-center gap-1 bg-transparent p-0 text-sm font-medium text-cmp-ink-2 hover:text-cmp-ink focus:outline-none'
              : compact
                ? 'flex h-10 w-14 items-center justify-center rounded border border-cmp-line bg-transparent text-cmp-ink hover:bg-cmp-bronze-soft focus:outline-none focus-visible:ring-1 focus-visible:ring-cmp-bronze'
                : 'flex w-full items-center gap-2 rounded border border-cmp-line bg-transparent px-3 py-2 text-left text-sm text-cmp-ink hover:bg-cmp-bronze-soft focus:outline-none focus-visible:ring-1 focus-visible:ring-cmp-bronze'
          }
          aria-label="Switch account"
          title={currentName}
        >
          {!inline ? <Building2 className="h-4 w-4 shrink-0 text-cmp-ink-3" /> : null}
          {!compact && <span className="min-w-0 flex-1 truncate">{currentName}</span>}
          {!compact && <ChevronDown className="h-4 w-4 shrink-0 text-cmp-ink-3" />}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          side={compact ? 'right' : 'bottom'}
          sideOffset={6}
          className="z-[10050] min-w-[220px] border-cmp-line bg-cmp-raise p-1 text-cmp-ink shadow-cmp-e2"
        >
          {accounts.map((account) => {
            const active = account.id === selectedAccount?.id;
            return (
              <DropdownMenuItem
                key={account.id}
                onSelect={() => selectAccountById(account.id)}
                className="cursor-pointer gap-2 text-cmp-ink focus:bg-cmp-bronze-soft focus:text-cmp-ink"
              >
                <Check className={`h-4 w-4 shrink-0 ${active ? 'opacity-100' : 'opacity-0'}`} />
                <span className="truncate">{account.name}</span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {variant === 'settings' && (
        <p className="mt-1 text-xs text-muted-foreground">
          Settings below apply to the selected account.
        </p>
      )}
    </div>
  );
}
